import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Botao } from './base';
import { usePrenderFoco } from './sobreposicao';
import { passosDoTour, resolverPasso, type DomTour, type PassoTour } from './tour-passos';

const elementos = (alvo: string) => Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${alvo}"]`));
const temArea = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};
export const dom: DomTour = { existe: (a) => elementos(a).length > 0, visivel: (a) => elementos(a).some(temArea) };

/**
 * F9: tour de holofote no primeiro login. Cada passo aponta para o alvo visível; no celular abre a gaveta do menu
 * para os passos do menu, e um passo sem alvo visível aparece centralizado. Concluir, pular ou Esc chamam
 * `aoConcluir`, que grava o onboarding como concluído.
 */
export function Tour({
  aoConcluir,
  menuAberto,
  definirMenu,
  passos: passosDados,
}: {
  aoConcluir: () => void;
  menuAberto: boolean;
  definirMenu: (aberto: boolean) => void;
  /** U15 F2: passos próprios (as dicas da tela, já filtrados pelo alvo visível); sem eles valem os do primeiro login. */
  passos?: PassoTour[];
}) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  // Passos fixados na abertura: o menu recolhido do celular conta como existente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const passos = useMemo(() => passosDoTour(dom, passosDados), []);
  const passo = passos[Math.min(i, passos.length - 1)];
  const [posicao, setPosicao] = useState<{ passo: string; ret: DOMRect | null } | null>(null);
  const cartaoRef = useRef<HTMLDivElement>(null);
  // O foco entra no cartão a cada passo (a gaveta do celular também puxa o foco; o cartão vem depois e o recupera).
  const passoMostrado = posicao?.passo;
  // O cartão prende o foco (aria-modal): Tab circula só nos botões dele e o foco perdido volta para ele.
  usePrenderFoco(cartaoRef, !!passoMostrado, { focoInicial: false });
  useEffect(() => {
    if (!passoMostrado) return;
    const id = requestAnimationFrame(() => cartaoRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [passoMostrado]);

  useLayoutEffect(() => {
    if (!passo) return;
    const r = resolverPasso(passo, dom, menuAberto);
    if (r.menu === 'abrir' || r.menu === 'fechar') {
      definirMenu(r.menu === 'abrir');
      return;
    }
    const medir = () => {
      const el = r.alvo ? elementos(r.alvo).find(temArea) : undefined;
      if (el) el.scrollIntoView({ block: el.getBoundingClientRect().height > window.innerHeight / 2 ? 'nearest' : 'center' });
      setPosicao({ passo: passo.chave, ret: el?.getBoundingClientRect() ?? null });
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [passo, menuAberto, definirMenu]);

  const concluir = () => {
    definirMenu(false);
    aoConcluir();
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      definirMenu(false);
      aoConcluir();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [aoConcluir, definirMenu]);

  // Review r16 visual (r3v-interacoes): a altura do cartão é a medida, não a estimada (212 px). Com o texto do passo mais
  // longo, o cartão no rodapé do celular subia 7 px sobre o holofote; o holofote encolhe pela altura real.
  const [alturaMedida, setAlturaMedida] = useState(0);
  useLayoutEffect(() => {
    const h = cartaoRef.current?.offsetHeight ?? 0;
    if (h && Math.abs(h - alturaMedida) > 1) setAlturaMedida(h);
  }, [i, passoMostrado, alturaMedida]);

  if (!passo || posicao?.passo !== passo.chave) return null;
  const ret = posicao.ret;
  const ultimo = i >= passos.length - 1;
  const cartao = (
    <>
      <p className="kicker">{t('tour.passo', { atual: i + 1, total: passos.length })}</p>
      <h2 className="mt-1 font-semibold">{t(passo.titulo)}</h2>
      <p className="mt-1 text-sm text-tinta-2">{t(passo.texto)}</p>
      <div className="mt-4 flex justify-between gap-2">
        <Botao variante="fantasma" tamanho="sm" onClick={concluir}>
          {t('tour.pular')}
        </Botao>
        <Botao variante="primario" tamanho="sm" onClick={() => (ultimo ? concluir() : setI(i + 1))}>
          {ultimo ? t('tour.concluir') : t('tour.proximo')}
        </Botao>
      </div>
    </>
  );

  // Sem alvo visível: passo centralizado, sem âncora.
  if (!ret) {
    return createPortal(
      <div className="fixed inset-0 z-60 flex items-center justify-center bg-veu p-4" role="dialog" aria-modal="true" aria-label={t(passo.titulo)} data-tour-passo={passo.chave}>
        <div ref={cartaoRef} tabIndex={-1} className="w-80 max-w-full rounded-xl border border-borda bg-sup-1 p-4 shadow-2xl focus:outline-none">
          {cartao}
        </div>
      </div>,
      document.body,
    );
  }

  const folga = 6;
  let caixa = { top: ret.top - folga, left: ret.left - folga, width: ret.width + folga * 2, height: Math.max(ret.height, 24) + folga * 2 };
  const largura = Math.min(320, window.innerWidth - 24);
  const alturaCartao = Math.max(212, alturaMedida);
  const fimCaixa = caixa.top + caixa.height;
  // Cartão sem cobrir o alvo: abaixo, acima, à direita (menu lateral no desktop) ou, sem espaço, no rodapé da tela.
  const lugar: CSSProperties =
    window.innerHeight - fimCaixa >= alturaCartao
      ? { top: fimCaixa + 12, left: Math.min(Math.max(12, caixa.left), window.innerWidth - largura - 12) }
      : caixa.top >= alturaCartao
        ? { bottom: window.innerHeight - caixa.top + 12, left: Math.min(Math.max(12, caixa.left), window.innerWidth - largura - 12) }
        : window.innerWidth - (caixa.left + caixa.width) >= largura + 24
          ? { top: Math.min(Math.max(12, caixa.top), window.innerHeight - alturaCartao), left: caixa.left + caixa.width + 12 }
          : { bottom: 12, left: Math.max(12, (window.innerWidth - largura) / 2) };
  // Alvo alto (menu lateral no celular) sem espaço ao lado: o cartão fica no rodapé e o holofote encolhe para o trecho
  // acima dele, para que o cartão nunca cubra a área destacada.
  const cartaoNoRodape = 'bottom' in lugar && lugar.left === Math.max(12, (window.innerWidth - largura) / 2) && caixa.top < alturaCartao;
  if (cartaoNoRodape) caixa = { ...caixa, top: Math.max(caixa.top, 8), height: Math.max(72, Math.min(caixa.height, window.innerHeight - alturaCartao - 24 - Math.max(caixa.top, 8))) };

  return createPortal(
    <div className="fixed inset-0 z-60" role="dialog" aria-modal="true" aria-label={t(passo.titulo)} data-tour-passo={passo.chave}>
      <div className="absolute rounded-xl ring-2 ring-acento-forte transition-all" style={{ ...caixa, boxShadow: '0 0 0 9999px var(--veu)' }} />
      <div ref={cartaoRef} tabIndex={-1} className="absolute rounded-xl border border-borda bg-sup-1 p-4 shadow-2xl focus:outline-none" style={{ width: largura, ...lugar }}>
        {cartao}
      </div>
    </div>,
    document.body,
  );
}
